import { createReducer, on } from '@ngrx/store';
import { TourType } from '../../data/tour-config';
import { TourProgress } from '../../models/tour-progress';
import {
  loadTourProgress,
  loadTourProgressFailure,
  loadTourProgressSuccess,
  upsertTourProgress,
  upsertTourProgressFailure,
  upsertTourProgressSuccess,
} from '../actions/tour-progress.actions';

export const tourProgressFeatureKey = 'tour-progress';

interface PendingRollback {
  previous: TourProgress | null;
  optimistic: TourProgress;
}

export interface TourProgressState {
  progress: TourProgress[];
  isLoaded: boolean;
  pendingRollback: Partial<Record<TourType, PendingRollback[]>>;
}

export const initialState: TourProgressState = {
  progress: [],
  isLoaded: false,
  pendingRollback: {},
};

function upsertProgress(progress: TourProgress[], updated: TourProgress): TourProgress[] {
  const others = progress.filter(p => p.tourTypeId !== updated.tourTypeId);
  return [...others, updated];
}

function removeProgress(progress: TourProgress[], tourType: TourType): TourProgress[] {
  return progress.filter(p => p.tourTypeId !== tourType);
}

function pushPendingRollback(
  pendingRollback: TourProgressState['pendingRollback'],
  tourType: TourType,
  previous: TourProgress | null,
  optimistic: TourProgress
): TourProgressState['pendingRollback'] {
  return {
    ...pendingRollback,
    [tourType]: [...(pendingRollback[tourType] ?? []), { previous, optimistic }],
  };
}

function shiftPendingRollback(
  pendingRollback: TourProgressState['pendingRollback'],
  tourType: TourType
): { pendingRollback: TourProgressState['pendingRollback']; rollback: PendingRollback | null } {
  const [rollback = null, ...remaining] = pendingRollback[tourType] ?? [];
  const updated = { ...pendingRollback };

  if (remaining.length > 0) {
    updated[tourType] = remaining;
  } else {
    delete updated[tourType];
  }

  return { pendingRollback: updated, rollback };
}

function replaceQueuedPrevious(
  pendingRollback: TourProgressState['pendingRollback'],
  tourType: TourType,
  from: TourProgress,
  to: TourProgress | null
): TourProgressState['pendingRollback'] {
  const queue = pendingRollback[tourType];
  if (!queue) {
    return pendingRollback;
  }

  return {
    ...pendingRollback,
    [tourType]: queue.map(entry => (entry.previous === from ? { ...entry, previous: to } : entry)),
  };
}

export const tourProgressReducer = createReducer<TourProgressState>(
  initialState,

  on(loadTourProgress, (state): TourProgressState => {
    return { ...state, isLoaded: false, pendingRollback: {} };
  }),

  on(loadTourProgressSuccess, (state, { progress }): TourProgressState => {
    return { ...state, progress, isLoaded: true, pendingRollback: {} };
  }),

  // Fail closed: leave isLoaded false so no tours are shown this session.
  on(loadTourProgressFailure, (state): TourProgressState => {
    return { ...state, progress: [], isLoaded: false, pendingRollback: {} };
  }),

  // Optimistic update so the gate immediately treats the tour as seen. lastSeenOnUtc is left unset
  // rather than faked — the server owns it and supplies the real value on success.
  on(upsertTourProgress, (state, { tourType, seenVersion, status }): TourProgressState => {
    const previous = state.progress.find(p => p.tourTypeId === tourType) ?? null;
    const optimistic = {
      tourTypeId: tourType,
      seenVersion,
      tourProgressStatusTypeId: status,
    };

    return {
      ...state,
      pendingRollback: pushPendingRollback(state.pendingRollback, tourType, previous, optimistic),
      progress: upsertProgress(state.progress, optimistic),
    };
  }),

  // Reconcile with the authoritative server record (e.g. lastSeenOnUtc).
  on(upsertTourProgressSuccess, (state, { progress }): TourProgressState => {
    const rollback = shiftPendingRollback(state.pendingRollback, progress.tourTypeId);
    return {
      ...state,
      pendingRollback: rollback.rollback
        ? replaceQueuedPrevious(rollback.pendingRollback, progress.tourTypeId, rollback.rollback.optimistic, progress)
        : rollback.pendingRollback,
      progress: upsertProgress(state.progress, progress),
    };
  }),

  // Roll the optimistic write back to what the server last confirmed. Without this the store keeps
  // claiming the tour is seen while the server never recorded it, so nothing retries and the write is
  // lost at the end of the session.
  on(upsertTourProgressFailure, (state, { tourType }): TourProgressState => {
    const rollback = shiftPendingRollback(state.pendingRollback, tourType);
    const pendingRollback = rollback.rollback
      ? replaceQueuedPrevious(
          rollback.pendingRollback,
          tourType,
          rollback.rollback.optimistic,
          rollback.rollback.previous
        )
      : rollback.pendingRollback;
    const previous = rollback.rollback?.previous ?? null;

    return {
      ...state,
      pendingRollback,
      progress: previous ? upsertProgress(state.progress, previous) : removeProgress(state.progress, tourType),
    };
  })
);
